// 🔄 화면(웹) 버전 가드 (2026-08-22 사용자 — "업데이트 확인을 눌러도 옛 화면이 나온다").
//   앱(APK/AAB)·데스크톱·웹 모두 같은 웹 번들을 로드하는데, 앱 웹뷰는 cold start 때만 loadUrl 을 하고
//   웹뷰 안에서는 visibilitychange 도 못 받는 경우가 있어 배포 후에도 옛 화면이 며칠씩 남았다.
//   서버 최신 웹 버전(/api/tpt/releases 선두 항목)과 __APP_VERSION__ 을 비교해 다르면 새로고침한다 —
//   index.html 이 no-cache(nginx) 라 reload 한 번이면 항상 최신 번들이 실린다.
//   신호원: ① visibilitychange(브라우저/데스크톱) ② 'tpt:app-foreground'(APK code 69+ 가 5분+ 백그라운드
//   복귀 시 evaluateJavascript 로 발사) ③ 더보기 [업데이트 확인] 수동 호출(checkWebVersion).

const CHECK_THROTTLE_MS = 10 * 60_000;   // 자동 확인 간격 하한
const MIN_AWAY_MS = 5 * 60_000;          // 잠깐(5분 미만) 이탈은 확인하지 않음
let lastCheckAt = 0;
let hiddenAt = 0;

function cmpVer(a: string, b: string): number {
	const pa = a.split('.').map(Number);
	const pb = b.split('.').map(Number);
	for (let i = 0; i < 3; i++) { const d = (pa[i] || 0) - (pb[i] || 0); if (d) return d; }
	return 0;
}

// 통화·화면공유 중 새로고침 금지 — 세션이 끊긴다 (CallDock/RoomShareDock 렌더 여부로 판정)
function inLiveSession(): boolean {
	try { return !!document.querySelector('[data-testid="call-dock"], [data-testid="roomshare-host-bar"], [data-testid="roomshare-viewer"]'); } catch { return false; }
}

async function fetchLatestWebVersion(): Promise<string | null> {
	try {
		const r = await fetch('/api/tpt/releases', { headers: { Accept: 'application/json' } });
		if (!r.ok) return null;
		const j = await r.json().catch(() => null) as { releases?: Array<{ version?: string }> } | null;
		const v = j?.releases?.[0]?.version;
		return v && /^\d+\.\d+\.\d+$/.test(v) ? v : null;
	} catch { return null; }
}

/**
 * 서버 최신 웹 버전 확인 — 더 새 버전이면 새로고침. 반환: 'reloading' | 'latest' | 'in-call' | 'unknown'.
 * manual=true(사용자 버튼)는 스로틀 무시. 같은 대상 버전으로의 reload 는 세션당 1회(루프 방지).
 */
export async function checkWebVersion(manual = false, opts: { ignoreThrottle?: boolean } = {}): Promise<'reloading' | 'latest' | 'in-call' | 'unknown'> {
	if (!manual && !opts.ignoreThrottle && Date.now() - lastCheckAt < CHECK_THROTTLE_MS) return 'unknown';
	lastCheckAt = Date.now();
	const latest = await fetchLatestWebVersion();
	if (!latest) return 'unknown';
	if (cmpVer(latest, __APP_VERSION__) <= 0) return 'latest';
	if (inLiveSession()) return 'in-call';
	try {
		// 루프 가드 — reload 뒤에도 서버가 옛 번들을 주는 비정상 상황에서 무한 새로고침 방지
		const k = 'tpt-ver-reload';
		if (!manual && sessionStorage.getItem(k) === latest) return 'unknown';
		sessionStorage.setItem(k, latest);
	} catch { /* private 모드 — 가드 없이 진행 */ }
	window.location.reload();
	return 'reloading';
}

function isTauriEnv(): boolean {
	return typeof window !== 'undefined' && ('__TAURI_INTERNALS__' in window || '__TAURI__' in window);
}

/**
 * 🔐 로그인 화면 전용 감시 (2026-08-25 사용자 — "앱 로그인 창에서는 업데이트가 안 된다").
 *   위 신호 3종은 모두 '로그인 후' 또는 '이탈 후 복귀'에만 발사돼, 로그인 화면에 머무는 사용자(예: 새 비번
 *   규칙 문구 불일치로 비번 변경이 400 나서 입장 못 하던 인원)는 수정판이 배포돼도 옛 번들에 갇혔다.
 *   마운트 즉시 + 5분 주기로 확인 — 웹/APK 는 서버 웹버전 비교 후 새로고침(스로틀 무시, 루프 가드는 유지),
 *   데스크톱(Tauri)은 번들이 로컬이라 새로고침이 무의미 → 'tpt:updater-check' 로 UpdaterBanner 확인을 강제한다.
 *   로그인 화면엔 잃을 세션·입력이 없어(마운트 직후·5분 간격) 새로고침 비용이 없다. 반환 = 정리 함수.
 */
export const LOGIN_WATCH_MS = 5 * 60_000;
export function startLoginVersionWatch(): () => void {
	if (typeof window === 'undefined') return () => undefined;
	const tick = () => {
		if (isTauriEnv()) { window.dispatchEvent(new Event('tpt:updater-check')); return; }
		void checkWebVersion(false, { ignoreThrottle: true });
	};
	tick();
	const iv = window.setInterval(tick, LOGIN_WATCH_MS);
	return () => window.clearInterval(iv);
}

/** main.tsx 에서 1회 설치 — 전면 복귀 신호를 구독한다. */
export function installVersionGuard(): void {
	if (typeof window === 'undefined' || typeof document === 'undefined') return;
	document.addEventListener('visibilitychange', () => {
		if (document.visibilityState === 'hidden') { hiddenAt = Date.now(); return; }
		if (!hiddenAt || Date.now() - hiddenAt < MIN_AWAY_MS) return;
		void checkWebVersion(false);
	});
	// APK 네이티브(code 69+) — 5분+ 백그라운드 복귀 시 발사. 이탈 시간 게이트는 네이티브가 이미 적용.
	window.addEventListener('tpt:app-foreground', () => { void checkWebVersion(false); });
}
